import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useState } from 'react';
import { useUserAutocompleteQuery } from '../../../../queries/Admin/Account/useUserAutocompleteQuery';
import {
	Autocomplete,
	AutocompleteContent,
	AutocompleteControl,
	AutocompleteInput,
	AutocompleteItem,
	AutocompleteList,
} from '../../../../web/components-v2/Autocomplete';
import { Button } from '../../../../web/components-v2/Button';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { noteCcFieldStyles as s } from './noteCcFieldStyles';

const userDisplayName = (user: Api.IUser): string =>
	`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.primaryEmail?.value || user.id;

/**
 * Maps selected CC users to a note's `referencedEntities.users` (method `Explicit`) — the shape the
 * legacy composer sent. The server sends each referenced employee the note by email; there is no
 * separate client send. Returns `undefined` when there are no CC recipients.
 */
export const buildCcReferencedEntities = (ccUsers: Api.IUser[]): Api.IRichContentReferencedEntities | undefined =>
	ccUsers.length
		? {
				companies: [],
				contacts: [],
				users: ccUsers.map(user => ({ entity: user, method: Api.RichContentReferenceMethod.Explicit })),
			}
		: undefined;

/** Debounced internal-employee typeahead that adds a user to the CC list (and clears its input). */
const CcAutocomplete = ({ selectedIds, onSelect }: { selectedIds: string[]; onSelect: (user: Api.IUser) => void }) => {
	const [inputValue, setInputValue] = useState('');
	const [debouncedFragment, setDebouncedFragment] = useState('');

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedFragment(inputValue), 250);
		return () => clearTimeout(handle);
	}, [inputValue]);

	const { data } = useUserAutocompleteQuery({ fragment: debouncedFragment, preventImpersonation: true });
	const users = (data?.values ?? []).filter(user => !selectedIds.includes(user.id));

	// base-ui's Autocomplete force-fills the input with the pressed item's value (the user id) on
	// selection. The click handler already added that user, so when the incoming value is a listed
	// user's id, clear the input instead of leaving the raw id in it; otherwise it's typed text.
	const onInputValueChange = (value: string) => {
		setInputValue(users.some(user => user.id === value) ? '' : value);
	};

	return (
		<Autocomplete items={users} value={inputValue} onValueChange={onInputValueChange}>
			<AutocompleteControl styles={[s.control]}>
				<AutocompleteInput placeholder='CC an employee…' />
			</AutocompleteControl>
			<AutocompleteContent>
				<AutocompleteList>
					{users.map(user => (
						<AutocompleteItem key={user.id} value={user.id} onClick={() => onSelect(user)}>
							{userDisplayName(user)}
						</AutocompleteItem>
					))}
				</AutocompleteList>
			</AutocompleteContent>
		</Autocomplete>
	);
};

export interface INoteCcFieldProps {
	/** The currently CC'd internal employees. */
	ccUsers: Api.IUser[];
	/** Reports the next CC list (add / remove). */
	onChange: (users: Api.IUser[]) => void;
}

/**
 * "Add Cc" affordance for the note composers. Collapsed to a link until expanded (matching the
 * legacy default-hidden behavior); once open, shows the selected internal employees as removable
 * tokens plus a typeahead to add more. CC'd employees are emailed the note server-side — see
 * {@link buildCcReferencedEntities}.
 */
export const NoteCcField = ({ ccUsers, onChange }: INoteCcFieldProps) => {
	const [expanded, setExpanded] = useState(ccUsers.length > 0);

	if (!expanded) {
		return (
			<Button variant='link' styles={[s.addCc]} onClick={() => setExpanded(true)}>
				Add Cc
			</Button>
		);
	}

	return (
		<div className={css(s.field)}>
			<span className={css(s.label)}>Cc (internal employees)</span>
			{ccUsers.length > 0 && (
				<div className={css(s.tokens)}>
					{ccUsers.map(user => (
						<span key={user.id} className={css(s.token)}>
							<span>{userDisplayName(user)}</span>
							<Button
								variant='ghost'
								size='icon'
								styles={[s.removeToken]}
								aria-label={`Remove ${userDisplayName(user)}`}
								onClick={() => onChange(ccUsers.filter(u => u.id !== user.id))}
							>
								<XIcon width={10} height={10} />
							</Button>
						</span>
					))}
				</div>
			)}
			<CcAutocomplete selectedIds={ccUsers.map(u => u.id)} onSelect={user => onChange([...ccUsers, user])} />
		</div>
	);
};
